<!--
  Sign in.

  There is no "create an account" link, and its absence is the design rather than an omission:
  accounts are invite-only. Anyone who can sign in can write boxes, and every box carries their
  name in a dataset a scholar may eventually cite — so an admin decides who gets in, and the
  page says so instead of leaving a visitor hunting for a button that does not exist.

  The form deliberately does not tell a failed sign-in which half was wrong. The service
  answers "that email and password combination is not valid" for an unknown address and for a
  bad password alike, in the same time; echoing anything more here would undo that.
-->
<script lang="ts">
  import { api } from '../lib/api/client';
  import { asProblem } from '../lib/api/problem';
  import { router } from '../lib/router.svelte';
  import { auth } from '../lib/stores/auth.svelte';

  let email = $state('');
  let password = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  // Shown once. Clearing it on the first keystroke stops "your session ended" hanging over a
  // deliberate second sign-in as though it had just happened again.
  const endedReason = $derived(auth.endedReason);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    error = null;
    busy = true;
    try {
      const payload = await api.login(email.trim(), password);
      auth.accept(payload);
      // Not a redirect back to where they were: a token expiry can strike mid-inscription,
      // and dropping somebody straight back into a canvas they were locked out of a moment
      // ago is worse than landing them on the home page with their bearings.
      router.go({ name: 'home', tab: 'dashboard' });
    } catch (caught) {
      error = asProblem(caught).message;
      password = '';
    } finally {
      busy = false;
    }
  }
</script>

<div class="page">
  <form class="panel" onsubmit={submit}>
    <h1>Sign in</h1>

    {#if endedReason && !error}
      <p class="notice" role="status">{endedReason}</p>
    {/if}

    <label>
      <span>Email</span>
      <!-- svelte-ignore a11y_autofocus -->
      <input
        type="email"
        autocomplete="username"
        bind:value={email}
        required
        autofocus
        oninput={() => {
          error = null;
          auth.endedReason = null;
        }}
      />
    </label>

    <label>
      <span>Password</span>
      <input
        type="password"
        autocomplete="current-password"
        bind:value={password}
        required
        oninput={() => (error = null)}
      />
    </label>

    {#if error}
      <p class="error" role="alert">{error}</p>
    {/if}

    <button class="primary" type="submit" disabled={busy || !email || !password}>
      {busy ? 'Signing in…' : 'Sign in'}
    </button>

    <p class="muted small">
      Accounts are created by invitation. Anyone who can sign in can draw boxes, and every box
      carries their name permanently — so an administrator issues a one-time link rather than
      leaving registration open. Ask one for an invite.
    </p>
  </form>
</div>

<style>
  .page {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 48px 16px;
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    max-width: 380px;
  }

  h1 {
    margin: 0;
    font-size: 1.3rem;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  label span {
    font-size: 0.85rem;
  }

  .notice,
  .error {
    margin: 0;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .notice {
    border: 1px solid var(--warn, #b8860b);
  }

  .error {
    border: 1px solid var(--danger, #b3261e);
  }

  .small {
    font-size: 0.82rem;
    line-height: 1.5;
  }
</style>
